import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../redux/store';
import { useSelector } from '../redux/hooks';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 40px;
  background: #ffffff;
`;

const Title = styled.h1`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 32px;
  color: #a13b16;
  text-align: center;
  margin-bottom: 30px;
`;

const FlexRow = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;
`;

const List = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-content: flex-start;
  row-gap: 10px;
`;

const ListItem = styled.div<{ $isHovered: boolean }>`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => props.$isHovered ? '#f0e6e1' : '#f8f8f8'};
  white-space: nowrap;
`;

const Form = styled.form`
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Input = styled.input`
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
`;

const Button = styled.button`
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: #a13b16;
  color: white;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
`;

const DangerButton = styled(Button)`
  background: #c0392b;
`;

const CancelButton = styled(Button)`
  background: #999;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

interface Publisher {
  publisher_id: number;
  name: string;
}

const PublisherListView: React.FC = () => {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [selectedPublisher, setSelectedPublisher] = useState<Publisher | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BASE_API_URL}/publishers`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(data => setPublishers(data))
      .catch(err => console.error('Failed to fetch publishers:', err));
  }, []);

  const handleSelect = useCallback((publisher: Publisher) => {
    setSelectedPublisher(publisher);
    setNameInput(publisher.name);
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!nameInput.trim()) return;

    const method = selectedPublisher ? "PUT" : "POST";
    const url = selectedPublisher
      ? `${process.env.REACT_APP_BASE_API_URL}/publishers/${selectedPublisher.publisher_id}`
      : `${process.env.REACT_APP_BASE_API_URL}/publishers`;

    fetch(url, {
      method,
      credentials: 'include',
      headers: { 
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ name: nameInput })
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (selectedPublisher) {
          setPublishers(prev => prev.map(p => p.publisher_id === data.publisher_id ? data : p));
        } else {
          setPublishers(prev => [...prev, data]);
        }
      })
      .catch(err => console.error('Failed to save publisher:', err));

    setSelectedPublisher(null);
    setNameInput('');
  }, [accessToken, nameInput, selectedPublisher]);

  const handleDelete = useCallback(() => {
    if (!selectedPublisher) return;

    fetch(`${process.env.REACT_APP_BASE_API_URL}/publishers/${selectedPublisher.publisher_id}`, {
      method: "DELETE",
      credentials: 'include',
      headers: { 
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      }
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        setPublishers(prev => prev.filter(p => p !== selectedPublisher));
        return res.json();
      })
      .catch(err => console.error('Failed to delete publisher:', err));

    setSelectedPublisher(null);
    setNameInput('');
  }, [accessToken, selectedPublisher]);

  const handleCancel = useCallback(() => {
    setSelectedPublisher(null);
    setNameInput('');
  }, []);

  return (
    <PageContainer>
      <TopBar>
        <Title>Видавництва</Title>
        <Button onClick={() => navigate('/admin')}>Головна</Button>
      </TopBar>
      <FlexRow>
        <List>
          {publishers.map(publisher => (
            <ListItem
              key={publisher.publisher_id}
              $isHovered={hoveredItem === publisher.name}
              onClick={() => handleSelect(publisher)}
              onMouseEnter={() => setHoveredItem(publisher.name)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              {publisher.name}
            </ListItem>
          ))}
        </List>

        <Form onSubmit={handleSubmit}>
          <Input
            placeholder="Назва видавництва"
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
          />
          <Button type="submit">
            {selectedPublisher ? 'Зберегти' : 'Створити'}
          </Button>
          {selectedPublisher && (
            <>
              <DangerButton type="button" onClick={handleDelete}>
                Видалити
              </DangerButton>
              <CancelButton type="button" onClick={handleCancel}>
                Відмінити
              </CancelButton>
            </>
          )}
        </Form>
      </FlexRow>
    </PageContainer>
  );
};

export default PublisherListView;